import { UnionKeyOf } from '@omnea-digital/toolbox'
import { containsOnlyOne } from 'utils/general/arrays'
import { isDefined } from './isDefined'

type WithPropertyDefined<T, P extends UnionKeyOf<T>> = T extends object
  ? P extends keyof T
    ? Omit<T, P> & {
        [Prop in P]-?: NonNullable<T[Prop]>
      }
    : never
  : never

/**
 * Function to be used as a type guard to check if a property is defined
 * It narrows the type of the input object so that the property is not optional anymore
 * It also deals with unions of objects, it completely removes options that don't have the property defined
 *
 * It can be used both as a single function call or a curried function to be used in iterating filters
 */
function propertyIsDefined<T, P extends UnionKeyOf<T>>(property: P): (object: T) => object is WithPropertyDefined<T, P>
function propertyIsDefined<T, P extends UnionKeyOf<T>>(object: T, property: P): object is WithPropertyDefined<T, P>
function propertyIsDefined<T, P extends UnionKeyOf<T>>(...args: [P] | [T, P]) {
  if (containsOnlyOne(args)) {
    const [property] = args

    return (object: T): object is WithPropertyDefined<T, P> => {
      return propertyIsDefined(object, property)
    }
  }

  const [object, property] = args

  return !!object && typeof object === 'object' && isDefined(object[property])
}

export { propertyIsDefined }
